import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  Spinner,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SearchableSelect from "../SearchableSelect";

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";

  const date = new Date(isoString);
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
};

const MobileField = ({ label, children }) => (
  <Box minW={0}>
    <Text display={{ base: "block", md: "none" }} fontSize="xs" color="gray.500" mb={1}>{label}</Text>
    {children}
  </Box>
);

export default function WaitlistCalendarOpenings({
  title = "Calendar Openings",
  subtitle = null,
  syncComplete = false,
  syncPhase = null,
  noWaitlistCustomersYet = false,
  showGenerateButton = true,
  initialFilledFilter = "all",
  includeFilledHistory = false,
  showFilledAt = false,
  showFilledColumn = showFilledAt,
  readOnly = false,
  emptyMessage = null,
  providerLabel = "provider",
  waitlistSignalLabel = "waitlist setting",
}) {
  const navigate = useNavigate();
  const toast = useToast();

  const [openingsLoading, setOpeningsLoading] = useState(true);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [openings, setOpenings] = useState([]);
  const [openingsPage, setOpeningsPage] = useState(1);
  const [openingsPagination, setOpeningsPagination] = useState(null);
  const [workerOptions, setWorkerOptions] = useState([]);

  const [automationFilter, setAutomationFilter] = useState("all");
  const [filledFilter, setFilledFilter] = useState(initialFilledFilter);
  const [recommendationsFilter, setRecommendationsFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hourFrom, setHourFrom] = useState("");
  const [hourTo, setHourTo] = useState("");
  const [workerFilters, setWorkerFilters] = useState([]);
  const gridColumns = showFilledColumn
    ? "1.1fr 0.9fr 0.9fr 1.1fr 0.8fr 0.9fr 1fr 1.1fr 0.9fr 0.9fr"
    : "1.1fr 0.9fr 0.9fr 1.1fr 0.8fr 0.9fr 1fr 1.1fr 0.9fr";

  useEffect(() => {
    fetchOpenings(1);
  }, [automationFilter, filledFilter, recommendationsFilter, dateFrom, dateTo, hourFrom, hourTo, workerFilters]);

  const fetchOpenings = async (page = 1) => {
    setOpeningsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page) });
      params.set("automation", automationFilter);
      params.set("filled", filledFilter);
      params.set("has_recommendations", recommendationsFilter);
      if (includeFilledHistory) params.set("include_filled_history", "true");
      if (dateFrom) params.set("starts_after", dateFrom);
      if (dateTo) params.set("starts_before", dateTo);
      if (hourFrom !== "") params.set("hour_from", hourFrom);
      if (hourTo !== "") params.set("hour_to", hourTo);
      workerFilters.forEach((worker) => params.append("workers[]", worker));

      const response = await fetch(`/api/waitlist_openings?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load openings");
      }
      setOpenings(data.openings || []);
      setOpeningsPagination(data.pagination || null);
      setWorkerOptions(data.workers || []);
      setOpeningsPage(page);
    } catch (error) {
      setOpenings([]);
      setOpeningsPagination(null);
      setWorkerOptions([]);
    } finally {
      setOpeningsLoading(false);
    }
  };

  const handleToggleAutomation = async (openingId, nextOn) => {
    const nextStatus = nextOn ? "on" : "off";
    setOpenings((prev) =>
      prev.map((opening) =>
        opening.id === openingId
          ? { ...opening, waitlist_automation_status: nextStatus }
          : opening
      )
    );

    try {
      const response = await fetch(`/api/waitlist_openings/${openingId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ waitlist_automation_status: nextStatus }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update automation status");
      }

      setOpenings((prev) =>
        prev.map((opening) => (opening.id === openingId ? data.opening : opening))
      );
    } catch (error) {
      setOpenings((prev) =>
        prev.map((opening) =>
          opening.id === openingId
            ? {
                ...opening,
                waitlist_automation_status: opening.waitlist_automation_status === "on" ? "off" : "on",
              }
            : opening
        )
      );
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleGenerateScheduleOpenings = async () => {
    setGenerateLoading(true);

    try {
      const response = await fetch("/api/waitlist_openings/generate_schedule_openings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to start schedule opening generation");
      }

      toast({
        title: "Generating schedule openings",
        description: "We started generating schedule openings from your synced calendar.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setGenerateLoading(false);
    }
  };

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 6 }} bg="white">
      <Flex justify="space-between" align="start" mb={4} flexWrap="wrap" gap={3}>
        <HStack align="start" spacing={3} flexWrap="wrap" w={{ base: "full", md: "auto" }}>
          <Box>
          <Heading size="sm">{title}</Heading>
          {subtitle && (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {subtitle}
            </Text>
          )}
          <Text fontSize="xs" color="gray.500" mt={2}>
            {syncComplete
              ? `Your ${providerLabel} sync is complete. You can generate schedule openings now.`
              : syncPhase || `Finish the ${providerLabel} sync before generating schedule openings.`}
          </Text>
          </Box>
          {showGenerateButton && (
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleGenerateScheduleOpenings}
              isLoading={generateLoading}
              isDisabled={!syncComplete || generateLoading}
              alignSelf="center"
              w={{ base: "full", sm: "auto" }}
              minH="44px"
            >
              Generate schedule openings
            </Button>
          )}
        </HStack>
        <HStack spacing={2} flexWrap="wrap" w={{ base: "full", md: "auto" }} align="stretch">
          <VStack align="start" spacing={1} minW={{ base: "100%", sm: "180px" }}>
            <Text fontSize="xs" color="gray.500">Automation</Text>
            <HStack spacing={1}>
              {[
                { value: "all", label: "All" },
                { value: "on", label: "On" },
                { value: "off", label: "Off" },
              ].map((option) => (
                <Button
                  key={option.value}
                  size="xs"
                  variant={automationFilter === option.value ? "solid" : "outline"}
                  colorScheme={automationFilter === option.value ? "blue" : "gray"}
                  onClick={() => setAutomationFilter(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </HStack>
          </VStack>
          <VStack align="start" spacing={1} minW={{ base: "100%", sm: "180px" }}>
            <Text fontSize="xs" color="gray.500">Has Recommendations</Text>
            <HStack spacing={1}>
              {[
                { value: "all", label: "All" },
                { value: "on", label: "Yes" },
                { value: "off", label: "No" },
              ].map((option) => (
                <Button
                  key={option.value}
                  size="xs"
                  variant={recommendationsFilter === option.value ? "solid" : "outline"}
                  colorScheme={recommendationsFilter === option.value ? "blue" : "gray"}
                  onClick={() => setRecommendationsFilter(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </HStack>
          </VStack>
          <VStack align="start" spacing={1} flex={{ base: "1 1 145px", md: "0 1 auto" }}>
            <Text fontSize="xs" color="gray.500">Starts After</Text>
            <Input
              size="sm"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              maxW={{ base: "full", md: "160px" }}
            />
          </VStack>
          <VStack align="start" spacing={1} flex={{ base: "1 1 145px", md: "0 1 auto" }}>
            <Text fontSize="xs" color="gray.500">Starts Before</Text>
            <Input
              size="sm"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              maxW={{ base: "full", md: "160px" }}
            />
          </VStack>
          <VStack align="start" spacing={1} flex={{ base: "1 1 120px", md: "0 1 auto" }}>
            <Text fontSize="xs" color="gray.500">Start Hour (24h)</Text>
            <Input
              size="sm"
              type="number"
              min={0}
              max={23}
              placeholder="0"
              value={hourFrom}
              onChange={(e) => setHourFrom(e.target.value)}
              maxW={{ base: "full", md: "100px" }}
            />
          </VStack>
          <VStack align="start" spacing={1} flex={{ base: "1 1 120px", md: "0 1 auto" }}>
            <Text fontSize="xs" color="gray.500">End Hour (24h)</Text>
            <Input
              size="sm"
              type="number"
              min={0}
              max={23}
              placeholder="23"
              value={hourTo}
              onChange={(e) => setHourTo(e.target.value)}
              maxW={{ base: "full", md: "100px" }}
            />
          </VStack>
          <VStack align="start" spacing={1} minW={{ base: "100%", sm: "240px" }}>
            <Text fontSize="xs" color="gray.500">Workers</Text>
            <SearchableSelect
              options={workerOptions}
              getOptionLabel={(worker) => worker}
              getOptionValue={(worker) => worker}
              selectedValues={workerFilters}
              onChange={setWorkerFilters}
              placeholder="Search workers..."
              multiSelect={true}
              size="sm"
            />
            {workerFilters.length > 0 && (
              <HStack spacing={2} flexWrap="wrap">
                {workerFilters.map((worker) => (
                  <Box
                    key={worker}
                    px={2}
                    py={1}
                    bg="blue.100"
                    borderRadius="md"
                    fontSize="xs"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Text>{worker}</Text>
                    <Box
                      as="button"
                      ml={1}
                      color="blue.700"
                      fontWeight="bold"
                      onClick={() => setWorkerFilters((prev) => prev.filter((name) => name !== worker))}
                    >
                      x
                    </Box>
                  </Box>
                ))}
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="blue"
                  onClick={() => setWorkerFilters([])}
                >
                  Clear all
                </Button>
              </HStack>
            )}
          </VStack>
        </HStack>
      </Flex>

      {noWaitlistCustomersYet && (
        <Box mb={4} border="1px solid" borderColor="blue.100" borderRadius="md" bg="blue.50" px={4} py={3}>
          <Text fontSize="sm" color="blue.900" fontWeight="semibold">
            Your waitlist is ready, but no customers have been added yet.
          </Text>
          <Text fontSize="sm" color="blue.800" mt={1}>
            Once your team starts marking the {waitlistSignalLabel} for customers who say yes, matches and texts will appear here.
          </Text>
        </Box>
      )}

      <Box position="relative">
        {openingsLoading && openings.length > 0 && (
          <HStack
            position="absolute"
            top={2}
            right={2}
            bg="whiteAlpha.900"
            px={2}
            py={1}
            borderRadius="md"
            spacing={2}
            zIndex={1}
          >
            <Spinner size="xs" color="blue.500" />
            <Text fontSize="xs" color="gray.600">Updating...</Text>
          </HStack>
        )}

        <Grid templateColumns={{ base: "1fr", lg: "1fr" }} gap={2} opacity={openingsLoading && openings.length > 0 ? 0.7 : 1}>
          <GridItem display={{ base: "none", md: "grid" }} gridTemplateColumns={gridColumns} gap={3} px={3} py={2} borderBottom="1px solid" borderColor="gray.200">
            <Text fontSize="xs" color="gray.500">Date</Text>
            <Text fontSize="xs" color="gray.500">Starts</Text>
            <Text fontSize="xs" color="gray.500">Ends</Text>
            <Text fontSize="xs" color="gray.500">Worker</Text>
            <Text fontSize="xs" color="gray.500">Duration</Text>
            <Text fontSize="xs" color="gray.500">Automation</Text>
            <Text fontSize="xs" color="gray.500">Recommendations</Text>
            <Text fontSize="xs" color="gray.500">Automatically Textable</Text>
            <Text fontSize="xs" color="gray.500">Texts Sent</Text>
            {showFilledColumn && (
              <Text fontSize="xs" color="gray.500">{showFilledAt ? "Filled At" : "Filled?"}</Text>
            )}
          </GridItem>

          {openings.map((item) => (
            <Grid
              key={item.id}
              templateColumns={{ base: "1fr", md: gridColumns }}
              gap={3}
              px={3}
              py={3}
              borderBottom="1px solid"
              borderColor="gray.100"
              alignItems="center"
              cursor="pointer"
              _hover={{ bg: "gray.50" }}
              onClick={() => navigate(`/waitlist/openings/${item.id}`)}
              borderWidth={{ base: "1px", md: "0 0 1px 0" }}
              borderRadius={{ base: "md", md: 0 }}
              mb={{ base: 3, md: 0 }}
            >
              <MobileField label="Date"><Text fontSize="sm" color="gray.700">{formatDate(item.start_time)}</Text></MobileField>
              <MobileField label="Starts"><Text fontSize="sm" color="gray.700">{formatTime(item.start_time)}</Text></MobileField>
              <MobileField label="Ends"><Text fontSize="sm" color="gray.700">{formatTime(item.end_time)}</Text></MobileField>
              <MobileField label="Worker"><Text fontSize="sm" color="gray.700" fontWeight={{ base: "semibold", md: "normal" }}>{item.worker_name || "—"}</Text></MobileField>
              <MobileField label="Duration"><Text fontSize="sm" color="gray.700">{item.duration_minutes} min</Text></MobileField>
              <MobileField label="Automation"><HStack
                spacing={2}
                onClick={(event) => event.stopPropagation()}
              >
                <Switch
                  aria-label={`Toggle automation for ${item.worker_name || "opening"} at ${item.start_time}`}
                  isChecked={item.waitlist_automation_status === "on"}
                  isDisabled={readOnly}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (readOnly) return;
                    handleToggleAutomation(item.id, e.target.checked);
                  }}
                  colorScheme="green"
                />
                <Text fontSize="sm" color="gray.600">
                  {item.waitlist_automation_status === "on" ? "On" : "Off"}
                </Text>
              </HStack></MobileField>
              <MobileField label="Recommendations"><HStack spacing={2} onClick={(event) => event.stopPropagation()}>
                <Text fontSize="sm" color="gray.700">{item.recommendations_count ?? 0}</Text>
                {!readOnly && (
                  <IconButton
                    size="xs"
                    variant="outline"
                    aria-label="Refresh recommendations"
                    icon={<RefreshCw size={14} />}
                    onClick={async (event) => {
                      event.stopPropagation();
                      try {
                        const response = await fetch(`/api/waitlist_openings/${item.id}/recommendations/refresh`, {
                          method: "POST",
                          headers: {
                            "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
                          },
                        });
                        const data = await response.json();
                        if (!response.ok || !data.success) {
                          throw new Error(data.error || "Failed to refresh recommendations");
                        }
                        fetchOpenings(openingsPage);
                      } catch (error) {
                        toast({
                          title: "Error",
                          description: error.message,
                          status: "error",
                          duration: 4000,
                          isClosable: true,
                        });
                      }
                    }}
                  />
                )}
              </HStack></MobileField>
              <MobileField label="Automatically Textable"><Text fontSize="sm" color="gray.700">{item.automatically_textable_count ?? 0}</Text></MobileField>
              <MobileField label="Texts Sent"><Text fontSize="sm" color="gray.700">{item.texts_sent_count ?? 0}</Text></MobileField>
              {showFilledColumn && (
                <MobileField label={showFilledAt ? "Filled At" : "Filled?"}><Text fontSize="sm" color="gray.700">{showFilledAt ? formatDateTime(item.filled_at) : (item.filled ? "Yes" : "No")}</Text></MobileField>
              )}
            </Grid>
          ))
          }

          {!openingsLoading && openings.length === 0 && (
            <Box py={6} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                {emptyMessage ||
                  (noWaitlistCustomersYet
                  ? "No openings are ready to text yet because no customers have been opted in."
                  : "No openings match your filters.")}
              </Text>
            </Box>
          )}

          {openingsLoading && openings.length === 0 && (
            <Box py={6} textAlign="center">
              <Spinner size="sm" color="blue.500" />
            </Box>
          )}
        </Grid>
      </Box>

      {openingsPagination && openingsPagination.total_pages > 1 && (
        <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={3}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchOpenings(openingsPage - 1)}
            isDisabled={openingsPage <= 1}
          >
            Previous
          </Button>

          <HStack spacing={1} flexWrap="wrap">
            {Array.from({ length: openingsPagination.total_pages }).map((_, index) => index + 1)
              .filter((page) =>
                page === 1 ||
                page === openingsPagination.total_pages ||
                (page >= openingsPage - 2 && page <= openingsPage + 2)
              )
              .map((page) => {
              const isActive = page === openingsPage;
              return (
                <Button
                  key={page}
                  size="sm"
                  variant={isActive ? "solid" : "outline"}
                  colorScheme={isActive ? "blue" : "gray"}
                  onClick={() => fetchOpenings(page)}
                >
                  {page}
                </Button>
              );
            })}
          </HStack>

          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchOpenings(openingsPage + 1)}
            isDisabled={openingsPage >= openingsPagination.total_pages}
          >
            Next
          </Button>
        </Flex>
      )}
    </Box>
  );
}
